/**
 * The first-run questionnaire.
 *
 * ## Six questions, and every one of them changes something
 *
 * That was the rule the script was written against, and it is what keeps the
 * flow short without making it thin: an answer that only tells AEGIS something
 * about the user, and changes nothing the user will see, does not earn a
 * screen. Every question here writes to a watchlist, a preference, or a rule
 * set.
 *
 * ## It runs over the app, not in front of it
 *
 * The app is already rendered behind this. That is a deliberate difference
 * from a signup funnel: the product is not being withheld pending answers, and
 * `Skip for now` on every screen means it.
 *
 * ## Presets are pre-fills, never figures
 *
 * The firm picker fills the numbers screen in and then requires the user to
 * confirm each one, because firms change terms without notice and a stale
 * number that reached the rule engine unread would be a wrong risk figure on
 * the screen a trader checks before sizing up. The copy says so plainly rather
 * than hiding it in a tooltip.
 *
 * ## Nothing here is a house style
 *
 * CLAUDE.md forbids one, and a questionnaire is the easiest place in a product
 * to smuggle one in — a pre-ticked box is a decision made on somebody's
 * behalf. Nothing is pre-selected except values a firm published, and those
 * are marked as such.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import {
  ACCOUNT_SIZES, INSTRUMENTS, findFirm, firmNumbers, prefillFromFirm,
  questionCount, resumeStep, stepsFor, type OnboardingAnswers, type StepId,
} from '@aegis/core';
import { fmtUsd } from '../lib/api.js';
import { api, ApiError } from '../lib/api.js';
import type { OnboardingAnswersDto, OnboardingFirm } from '../lib/api.js';

/* ── the instrument chips, grouped ──────────────────────────────────────── */

/**
 * Built from the instrument master rather than a list in this file.
 *
 * CLAUDE.md: the instrument universe is data, not code. A contract added to
 * the master appears here with no change to this screen, and no screen
 * anywhere contains "the symbols we support".
 */
const ASSET_GROUPS: ReadonlyArray<readonly [string, string]> = [
  ['equity_index', 'Equity index'],
  ['energy', 'Energy'],
  ['metal', 'Metals'],
  ['rate', 'Rates'],
  ['fx', 'FX'],
  ['crypto', 'Crypto'],
  ['agricultural', 'Agricultural'],
];

export function instrumentGroups(): ReadonlyArray<readonly [string, readonly string[]]> {
  return ASSET_GROUPS
    .map(([cls, label]) => [
      label,
      INSTRUMENTS.filter((i) => i.assetClass === cls).map((i) => i.root),
    ] as const)
    .filter(([, roots]) => roots.length > 0);
}

const TIMEFRAMES = ['1m', '5m', '15m', '1h', '1d'] as const;
const CHART_STYLES = [
  ['candles', 'Candles'], ['hollow', 'Hollow'], ['bars', 'Bars'], ['line', 'Line'],
] as const;

/** The device's own zone, offered for confirmation and never assumed. */
function detectedZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'exchange';
  } catch {
    return 'exchange';
  }
}

const money = (v: string | null): string => (v === null ? '' : v);

/**
 * A typed figure, or null for "no such limit".
 *
 * Blank is an ANSWER on this screen, not an omission — several evaluations
 * genuinely have no daily loss limit, and `0` would read as "you may not lose
 * anything". Currency symbols and thousands separators are stripped because
 * people paste these straight off a firm's terms page.
 */
export const num = (v: string): string | null => {
  const t = v.replace(/[$,\s]/g, '').trim();
  return t === '' ? null : t;
};

/**
 * How Settings and the nudge re-open a flow that App owns.
 *
 * A window event rather than a route, because the questionnaire is an overlay
 * over whatever screen you were on — routing to it would lose that, and a
 * shared store for one boolean is more machinery than the seam deserves.
 */
export const OPEN_ONBOARDING = 'aegis:onboarding:open';

export function openOnboarding(): void {
  window.dispatchEvent(new Event(OPEN_ONBOARDING));
}

/**
 * The nudge, for somebody who skipped.
 *
 * Skipping was answer number one in the design review, and this is the half
 * that makes it honest: a user who skips is not written off, and a user who
 * dismisses this is not asked again. Neither state blocks anything.
 */
export function FinishSetupNudge(): JSX.Element | null {
  const [show, setShow] = useState(false);

  useEffect(() => {
    let live = true;
    void api.onboarding()
      .then((s) => { if (live) setShow(s.skippedAt !== null && s.completedAt === null); })
      .catch(() => { /* Never a reason to interrupt somebody. */ });
    return () => { live = false; };
  }, []);

  if (!show) return null;
  return (
    <div className="ob-nudge">
      <b>Finish setting up?</b>
      <span>
        Six questions. They set your watchlist, your firm&rsquo;s limits and how your
        trading day is measured.
      </span>
      <span className="ob-grow" />
      <button type="button" className="btn btn-ghost compact" onClick={openOnboarding}>
        Pick up where you left off
      </button>
      <button type="button" className="ob-skip" onClick={() => setShow(false)}>Dismiss</button>
    </div>
  );
}

/* ── the flow ───────────────────────────────────────────────────────────── */

export function OnboardingFlow({ onDone }: { onDone: () => void }): JSX.Element | null {
  const [answers, setAnswers] = useState<OnboardingAnswers | null>(null);
  const [firms, setFirms] = useState<readonly OnboardingFirm[]>([]);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [kept, setKept] = useState<readonly string[]>([]);
  const cardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let live = true;
    void Promise.all([api.onboarding(), api.onboardingFirms()])
      .then(([state, list]) => {
        if (!live) return;
        const loaded = state.answers as unknown as OnboardingAnswers;
        setAnswers(loaded);
        // "Pick up where you left off" has to mean it. A first run — nothing
        // answered — still opens on the welcome screen.
        setStep(resumeStep(loaded));
        setFirms(list.firms);
      })
      .catch(() => { if (live) setError('Could not load your setup.'); });
    return () => { live = false; };
  }, []);

  const steps = useMemo(
    () => stepsFor(answers?.accountKind ?? null), [answers?.accountKind],
  );
  const current: StepId = steps[Math.min(step, steps.length - 1)] ?? 'welcome';

  /*
    Save on every step change rather than at the end.

    A dropped connection mid-flow then costs the current screen and nothing
    else, which is what makes "resumable across devices" true rather than
    aspirational. Failures are deliberately silent: a save that did not land is
    not a reason to interrupt somebody answering questions, and the next step
    will try again.
  */
  const persist = useCallback(
    (a: OnboardingAnswers, status: 'in_progress' | 'completed' | 'skipped') =>
      api.saveOnboarding(a as unknown as OnboardingAnswersDto, status),
    [],
  );

  const patch = useCallback((over: Partial<OnboardingAnswers>) => {
    setAnswers((a) => (a ? { ...a, ...over } : a));
  }, []);

  const go = useCallback((delta: number) => {
    setAnswers((a) => {
      if (a) void persist(a, 'in_progress').catch(() => {});
      return a;
    });
    setStep((s) => Math.max(0, Math.min(steps.length - 1, s + delta)));
    // Long forms scroll; a new question should start at its own top rather
    // than halfway down the previous one.
    cardRef.current?.scrollTo({ top: 0 });
  }, [persist, steps.length]);

  const finish = useCallback(async (status: 'completed' | 'skipped') => {
    if (!answers) return onDone();
    setBusy(true);
    setError(null);
    try {
      const res = await persist(answers, status);
      setKept(res.kept);
      if (status === 'skipped') return onDone();
      // Completing lands on the recap rather than closing, so the user sees
      // what changed before the flow disappears.
      setStep(steps.length - 1);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save your answers.');
    } finally {
      setBusy(false);
    }
  }, [answers, onDone, persist, steps.length]);

  // Escape is skip, not cancel: there is no state to discard, and a flow that
  // traps focus with no keyboard exit is a flow that fails an audit.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') void finish('skipped');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [finish]);

  if (!answers) return null;

  const asked = questionCount(answers.accountKind);
  // Inclusive of the current step: a first question that announces itself as
  // "0 of 6" reads as an off-by-one, because it is one.
  const questionIndex = steps.slice(0, step + 1).filter((s) => s !== 'welcome').length;
  const wide = current === 'numbers';

  return (
    <div className="ob-scrim" role="dialog" aria-modal="true" aria-label="Set up AEGIS">
      <div className={`ob-card${wide ? ' wide' : ''}`} ref={cardRef}>
        <div className="ob-progress">
          <i style={{ width: `${(step / (steps.length - 1)) * 100}%` }} />
        </div>

        {current !== 'welcome' && current !== 'done' && (
          <div className="ob-head">
            <span className="ob-count mono">{questionIndex} OF {asked}</span>
            <button type="button" className="ob-skip" onClick={() => void finish('skipped')}>
              Skip for now
            </button>
          </div>
        )}

        <div className="ob-body" key={current}>
          {error && <p className="ob-error">{error}</p>}
          <Screen
            id={current}
            answers={answers}
            firms={firms}
            kept={kept}
            patch={patch}
          />
        </div>

        <Foot
          id={current}
          busy={busy}
          answers={answers}
          onBack={() => go(-1)}
          onNext={() => go(1)}
          onSkip={() => void finish('skipped')}
          onFinish={() => void finish('completed')}
          onDone={onDone}
        />
      </div>
    </div>
  );
}

/* ── the screens ────────────────────────────────────────────────────────── */

interface ScreenProps {
  id: StepId;
  answers: OnboardingAnswers;
  firms: readonly OnboardingFirm[];
  kept: readonly string[];
  patch: (over: Partial<OnboardingAnswers>) => void;
}

function Screen({ id, answers: a, firms, kept, patch }: ScreenProps): JSX.Element {
  switch (id) {
    case 'welcome':
      return (
        <div className="ob-hero">
          <span className="ob-mark">&AElig;</span>
          <h2>Six questions,<br />about a minute</h2>
          <p className="ob-sub">
            Every answer changes something you will see. None of this is market
            research, and all of it lives in Settings afterwards.
          </p>
        </div>
      );

    case 'instruments':
      return (
        <>
          <h2>What do you trade?</h2>
          <p className="ob-sub">
            Pick the contracts you actually watch. These become your watchlist, and
            the first one becomes the chart that opens.
          </p>
          {instrumentGroups().map(([label, roots]) => (
            <div className="ob-group" key={label}>
              <span className="ob-label mono">{label}</span>
              <div className="ob-chips">
                {roots.map((r) => (
                  <button
                    key={r} type="button" className="ob-chip"
                    aria-pressed={a.instruments.includes(r)}
                    onClick={() => patch({
                      instruments: a.instruments.includes(r)
                        ? a.instruments.filter((x) => x !== r)
                        : [...a.instruments, r],
                    })}
                  >{r}</button>
                ))}
              </div>
            </div>
          ))}
          <p className="ob-note">Change any time — nothing here is permanent.</p>
        </>
      );

    case 'account':
      return (
        <>
          <h2>Whose money are you trading?</h2>
          <p className="ob-sub">This decides which limits AEGIS measures you against.</p>
          <div className="ob-cards">
            {([
              ['evaluation', 'A prop firm evaluation',
                'You are working toward a target under someone else’s rules.'],
              ['funded', 'A funded prop account',
                'You passed. The rules matter more now, not less.'],
              ['own', 'My own capital',
                'Your limits are yours to set, and AEGIS has no opinion about them.'],
              ['both', 'Both',
                'A funded account and your own account at the same time.'],
            ] as const).map(([id2, title, body]) => (
              <button
                key={id2} type="button" className="ob-optcard"
                aria-pressed={a.accountKind === id2}
                onClick={() => patch({ accountKind: id2 })}
              >
                <b>{title}</b><span>{body}</span>
              </button>
            ))}
          </div>
        </>
      );

    case 'firm':
      return (
        <>
          <h2>Which firm?</h2>
          <p className="ob-sub">
            This picks a starting set of numbers. You confirm every one of them on
            the next screen — <strong>AEGIS does not track firm terms for you.</strong>
          </p>
          <div className="ob-firms">
            {firms.map((f) => (
              <button
                key={f.id} type="button" className="ob-firm"
                aria-pressed={a.firm === f.id}
                onClick={() => patch(prefillFromFirm({
                  ...a, firm: f.id, accountSize: a.accountSize ?? '50000',
                }))}
              >
                {f.name}
                <small className="mono">{f.summary}</small>
              </button>
            ))}
          </div>
          <p className="ob-note">
            Presets are shown for confirmation only, and carry the month their terms
            were read. Firms change them; your copy is the one that counts.
          </p>
        </>
      );

    case 'numbers': {
      const firm = findFirm(a.firm);
      const size = Number(a.accountSize ?? 0);
      const preview = firmNumbers(firm, size);
      const daily = a.maxDailyLoss === null ? null : Number(a.maxDailyLoss);
      const dd = a.maxDrawdown === null ? null : Number(a.maxDrawdown);
      const perPoint = 100; // a 2-lot ES position, the example in the copy
      const room = [daily, dd].filter((v): v is number => v !== null && v > 0);
      const tightest = room.length > 0 ? Math.min(...room) : null;

      return (
        <>
          <h2>Confirm your account&rsquo;s numbers</h2>
          <p className="ob-sub">
            Read these from your firm&rsquo;s current terms, not ours. Everything AEGIS
            tells you about your risk comes from this screen.
          </p>
          {firm?.researched && (
            <p className="ob-note">
              {firm.name} terms as published in {firm.researched}. Check them against
              your own account.
            </p>
          )}

          <div className="ob-split">
            <div className="ob-fields">
              <div className="ob-field">
                <span className="ob-label mono">Account size</span>
                <div className="ob-chips">
                  {ACCOUNT_SIZES.map((s) => (
                    <button
                      key={s} type="button" className="ob-chip"
                      aria-pressed={Number(a.accountSize) === s}
                      onClick={() => patch(prefillFromFirm({ ...a, accountSize: String(s) }))}
                    >${s / 1000}k</button>
                  ))}
                </div>
                <input
                  className="ob-input mono" inputMode="decimal"
                  aria-label="Account size"
                  value={money(a.accountSize)}
                  onChange={(e) => patch(prefillFromFirm({
                    ...a, accountSize: num(e.target.value),
                  }))}
                />
              </div>

              <div className="ob-two">
                <Field label="Profit target" value={a.profitTarget}
                       onChange={(v) => patch({ profitTarget: v })} />
                <Field label="Max drawdown" value={a.maxDrawdown}
                       onChange={(v) => patch({ maxDrawdown: v })} />
              </div>

              <div className="ob-field">
                <span className="ob-label mono">Daily loss limit</span>
                <input
                  className="ob-input mono" inputMode="decimal"
                  aria-label="Daily loss limit"
                  placeholder="No daily loss limit"
                  value={money(a.maxDailyLoss)}
                  onChange={(e) => patch({ maxDailyLoss: num(e.target.value) })}
                />
                {/* Blank is an ANSWER here, not an omission. */}
                <span className="ob-why">
                  Several evaluations have none at all. Leave this blank for those —
                  entering <span className="mono">$0</span> would read as
                  &ldquo;you may not lose anything&rdquo;.
                </span>
              </div>

              <div className="ob-field">
                <span className="ob-label mono">Daily loss counts</span>
                <div className="ob-chips">
                  {([
                    ['realized', 'Closed trades only'],
                    ['realized_plus_unrealized', 'Includes open positions'],
                  ] as const).map(([v, label]) => (
                    <button key={v} type="button" className="ob-chip"
                            aria-pressed={a.dailyLossBasis === v}
                            onClick={() => patch({ dailyLossBasis: v })}>{label}</button>
                  ))}
                </div>
              </div>

              <div className="ob-field">
                <span className="ob-label mono">How the drawdown floor moves</span>
                <div className="ob-cards">
                  {([
                    ['static', 'Static', 'The floor never moves. Set once at your starting balance.', false],
                    ['trailing_eod', 'Trailing, end of day', 'The floor rises with your end-of-day balance and stays there.', false],
                    ['trailing_intraday', 'Trailing, intraday', 'The floor rises with any peak, including unrealised. A winning trade that comes back can fail the account.', true],
                  ] as const).map(([v, title, body, severe]) => (
                    <button
                      key={v} type="button"
                      className={`ob-optcard compact${severe ? ' severe' : ''}`}
                      aria-pressed={a.drawdownType === v}
                      onClick={() => patch({ drawdownType: v })}
                    >
                      <b>{title}</b><span>{body}</span>
                    </button>
                  ))}
                </div>
              </div>

              {a.drawdownType !== 'static' && (
                <div className="ob-field">
                  <span className="ob-label mono">
                    The floor stops trailing once the account reaches
                  </span>
                  <input
                    className="ob-input mono" inputMode="decimal"
                    aria-label="Trailing stops at"
                    placeholder="It keeps trailing"
                    value={money(a.drawdownFreezeAt)}
                    onChange={(e) => patch({ drawdownFreezeAt: num(e.target.value) })}
                  />
                  <span className="ob-why">
                    Most firms freeze the floor once you are far enough ahead.
                    {a.drawdownFreezeAt !== null && a.maxDrawdown !== null && (
                      <> Yours would then hold at{' '}
                        <span className="mono">
                          {fmtUsd(String(Number(a.drawdownFreezeAt) - Number(a.maxDrawdown)))}
                        </span>.
                      </>
                    )}
                    {' '}Leave it blank if yours does not.
                  </span>
                </div>
              )}
            </div>

            <aside className="ob-preview">
              <h4 className="mono">What this means today</h4>
              <Gauge label="Daily loss limit"
                     value={daily === null ? 'none' : fmtUsd(String(daily))} />
              <Gauge label="Drawdown floor" tone="caution"
                     value={dd === null ? '—' : fmtUsd(String(size - dd))} />
              <p>
                {tightest === null
                  ? 'Enter a limit and this fills in with what it leaves you.'
                  : (
                    <>
                      A 2-lot <span className="mono">ES</span> position would have{' '}
                      <span className="mono">{(tightest / perPoint).toFixed(2)} points</span>{' '}
                      of room before the tighter of the two is reached.
                    </>
                  )}
              </p>
              {a.drawdownType === 'trailing_intraday' && (
                <p className="ob-severe">
                  On an intraday floor that distance does not grow when the trade goes
                  your way — the floor comes up with it.
                </p>
              )}
              {preview.maxDrawdown !== null && a.maxDrawdown === null && (
                <p>Pick a size to fill these in from {firm?.name}.</p>
              )}
            </aside>
          </div>
        </>
      );
    }

    case 'limits':
      return (
        <>
          <h2>Your own limits</h2>
          <p className="ob-sub">
            All optional. AEGIS has no view on what a correct risk level is — these
            are the numbers you want to be told about, not the ones we think you
            should have.
          </p>
          <div className="ob-fields">
            <div className="ob-two">
              <Field label="Daily loss limit" value={a.ownDailyLoss}
                     placeholder="No limit"
                     onChange={(v) => patch({ ownDailyLoss: v })} />
              <Field label="Max risk per trade" value={a.ownRiskPerTrade}
                     placeholder="No limit"
                     onChange={(v) => patch({ ownRiskPerTrade: v })} />
            </div>
            <div className="ob-two">
              <Field label="Max open contracts" value={
                a.ownMaxContracts === null ? null : String(a.ownMaxContracts)
              } placeholder="No limit"
                     onChange={(v) => patch({ ownMaxContracts: v === null ? null : Number(v) })} />
              <Field label="Max trades per day" value={
                a.ownMaxTrades === null ? null : String(a.ownMaxTrades)
              } placeholder="No limit"
                     onChange={(v) => patch({ ownMaxTrades: v === null ? null : Number(v) })} />
            </div>
            <div className="ob-field">
              <span className="ob-label mono">Daily loss counts</span>
              <div className="ob-chips">
                {([
                  ['realized', 'Closed trades only'],
                  ['realized_plus_unrealized', 'Includes open positions'],
                ] as const).map(([v, label]) => (
                  <button key={v} type="button" className="ob-chip"
                          aria-pressed={a.dailyLossBasis === v}
                          onClick={() => patch({ dailyLossBasis: v })}>{label}</button>
                ))}
              </div>
            </div>
          </div>
        </>
      );

    case 'when': {
      const zone = detectedZone();
      return (
        <>
          <h2>When is your day?</h2>
          <div className="ob-group">
            <span className="ob-label mono">Timezone</span>
            <div className="ob-detect">
              <b className="mono">{zone}</b>
              <span>
                Detected from this device. Decides where a trading day starts and ends
                in your journal.
              </span>
            </div>
            <div className="ob-chips">
              <button type="button" className="ob-chip"
                      aria-pressed={a.timezone === zone}
                      onClick={() => patch({ timezone: zone })}>Use this</button>
              <button type="button" className="ob-chip"
                      aria-pressed={a.timezone === 'exchange'}
                      onClick={() => patch({ timezone: 'exchange' })}>
                Follow each exchange
              </button>
            </div>
          </div>
          <div className="ob-group">
            <span className="ob-label mono">Session</span>
            <div className="ob-cards">
              <button type="button" className="ob-optcard"
                      aria-pressed={a.sessionScope === 'rth'}
                      onClick={() => patch({ sessionScope: 'rth' })}>
                <b>Regular hours only</b>
                <span>Overnight trades sit outside your session statistics.</span>
              </button>
              <button type="button" className="ob-optcard"
                      aria-pressed={a.sessionScope === 'eth'}
                      onClick={() => patch({ sessionScope: 'eth' })}>
                <b>Include the overnight session</b>
                <span>
                  The full electronic day. Changes which trades count toward a session,
                  and what the chart shades.
                </span>
              </button>
            </div>
          </div>
        </>
      );
    }

    case 'chart':
      return (
        <>
          <h2>How do you read a chart?</h2>
          <p className="ob-sub">Where the chart opens, every time. One tap to change it later.</p>
          <div className="ob-group">
            <span className="ob-label mono">Opens on</span>
            <div className="ob-chips">
              {TIMEFRAMES.map((tf) => (
                <button key={tf} type="button" className="ob-tf"
                        aria-pressed={a.defaultTimeframe === tf}
                        onClick={() => patch({ defaultTimeframe: tf })}>{tf}</button>
              ))}
            </div>
          </div>
          <div className="ob-group">
            <span className="ob-label mono">Drawn as</span>
            <div className="ob-styles">
              {CHART_STYLES.map(([v, label]) => (
                <button key={v} type="button" className="ob-style"
                        aria-pressed={a.chartStyle === v}
                        onClick={() => patch({ chartStyle: v })}>
                  <StyleGlyph id={v} />
                  <span className="mono">{label}</span>
                </button>
              ))}
            </div>
          </div>
          <p className="ob-note">
            Footprint and depth-of-market become available once there is data behind
            them.
          </p>
        </>
      );

    case 'done': {
      const firm = findFirm(a.firm);
      const ddLabel = a.drawdownType === 'static' ? 'static'
        : a.drawdownType === 'trailing_eod' ? 'trailing, end of day'
          : a.drawdownType === 'trailing_intraday' ? 'trailing, intraday' : null;
      return (
        <>
          <div className="ob-hero">
            <span className="ob-tick" aria-hidden="true">&#10003;</span>
            <h2>That&rsquo;s it.</h2>
            <p className="ob-sub">Here is what changed. Every line is editable in Settings.</p>
          </div>
          <dl className="ob-recap">
            <Recap k="Watchlist" to="/settings"
                   v={a.instruments.length > 0 ? a.instruments.join(' · ') : 'Left as it was'}
                   note={a.instruments.length > 0
                     ? `The chart opens on ${a.instruments[0]}.`
                     : 'You picked no instruments, so nothing was changed.'} />
            {a.accountKind !== 'own' && a.maxDrawdown !== null && (
              <Recap k="Rule set" to="/settings"
                     v={[firm?.name, a.accountSize ? fmtUsd(a.accountSize) : null,
                       a.maxDrawdown ? `${fmtUsd(a.maxDrawdown)} ${ddLabel}` : null]
                       .filter(Boolean).join(' · ')}
                     note="Your figures, not the firm’s published ones. AEGIS measures against exactly these." />
            )}
            {a.accountKind === 'own' && (
              <Recap k="Your limits" to="/settings"
                     v={[a.ownDailyLoss ? `${fmtUsd(a.ownDailyLoss)} daily` : null,
                       a.ownMaxContracts ? `${a.ownMaxContracts} contracts` : null,
                       a.ownMaxTrades ? `${a.ownMaxTrades} trades` : null]
                       .filter(Boolean).join(' · ') || 'None set'}
                     note="Self-imposed. The same engine that watches a prop account watches these." />
            )}
            <Recap k="Your day" to="/settings/preferences"
                   v={[a.timezone ?? 'unchanged',
                     a.sessionScope === 'rth' ? 'regular hours'
                       : a.sessionScope === 'eth' ? 'including overnight' : null]
                     .filter(Boolean).join(' · ')} />
            <Recap k="Chart" to="/settings/preferences"
                   v={[a.defaultTimeframe, a.chartStyle].filter(Boolean).join(' · ') || 'unchanged'} />
          </dl>
          {kept.length > 0 && (
            /*
              Said out loud rather than left to be discovered. The user changed
              something by hand mid-flow; silently overwriting it would undo a
              deliberate, more recent decision, and silently keeping it would
              leave them thinking the questionnaire had taken effect.
            */
            <p className="ob-kept">
              <strong>Some settings were left alone.</strong> You had already changed{' '}
              {kept.join(', ')} by hand, so this did not overwrite them.
            </p>
          )}
          <p className="ob-note">
            How trades are matched, your breakeven band and the roll rule were not
            asked about, and are untouched. They are all in Settings.
          </p>
        </>
      );
    }

    default:
      return <></>;
  }
}

/* ── footers ────────────────────────────────────────────────────────────── */

function Foot({ id, busy, answers, onBack, onNext, onSkip, onFinish, onDone }: {
  id: StepId; busy: boolean; answers: OnboardingAnswers;
  onBack: () => void; onNext: () => void; onSkip: () => void;
  onFinish: () => void; onDone: () => void;
}): JSX.Element {
  if (id === 'welcome') {
    return (
      <div className="ob-foot">
        <button type="button" className="btn btn-primary" onClick={onNext}>Set it up</button>
        <span className="ob-grow" />
        <button type="button" className="btn btn-text" onClick={onSkip}>Skip for now</button>
      </div>
    );
  }
  if (id === 'done') {
    return (
      <div className="ob-foot">
        <button type="button" className="btn btn-primary" onClick={onDone}>
          Connect a broker
        </button>
        <button type="button" className="btn btn-ghost" onClick={onDone}>
          Import a journal
        </button>
        <span className="ob-grow" />
        <button type="button" className="btn btn-text" onClick={onDone}>Look around first</button>
      </div>
    );
  }
  const last = id === 'chart';
  return (
    <div className="ob-foot">
      <button type="button" className="btn btn-text" onClick={onBack}>Back</button>
      <span className="ob-grow" />
      {id === 'instruments' && (
        <span className="ob-count mono">{answers.instruments.length} selected</span>
      )}
      <button
        type="button" className="btn btn-primary" disabled={busy}
        onClick={last ? onFinish : onNext}
      >
        {busy ? 'Saving…' : last ? 'Finish' : id === 'numbers' ? 'These are right' : 'Continue'}
      </button>
    </div>
  );
}

/* ── small parts ────────────────────────────────────────────────────────── */

function Field({ label, value, placeholder, onChange }: {
  label: string; value: string | null; placeholder?: string;
  onChange: (v: string | null) => void;
}): JSX.Element {
  return (
    <div className="ob-field">
      <span className="ob-label mono">{label}</span>
      <input
        className="ob-input mono" inputMode="decimal" aria-label={label}
        placeholder={placeholder ?? ''}
        value={money(value)}
        onChange={(e) => onChange(num(e.target.value))}
      />
    </div>
  );
}

function Gauge({ label, value, tone }: {
  label: string; value: string; tone?: 'caution';
}): JSX.Element {
  return (
    <div className="ob-gauge">
      <span className="ob-gauge-name">{label}</span>
      <span className={`ob-gauge-val mono${tone === 'caution' ? ' caution' : ''}`}>{value}</span>
    </div>
  );
}

function Recap({ k, v, note, to }: {
  k: string; v: string; note?: string; to: string;
}): JSX.Element {
  return (
    <div className="ob-recap-row">
      <dt className="mono">{k}</dt>
      <dd>
        {v}
        {note && <em>{note}</em>}
      </dd>
      <a href={to}>Settings</a>
    </div>
  );
}

/** A miniature of the thing being chosen, rather than a word for it. */
function StyleGlyph({ id }: { id: string }): JSX.Element {
  const up = 'var(--market-up)';
  const down = 'var(--market-down)';
  if (id === 'line') {
    return (
      <svg width="42" height="26" viewBox="0 0 42 26" aria-hidden="true">
        <polyline points="2,20 10,14 18,17 26,8 34,11 40,4" fill="none" stroke={up} strokeWidth="2" />
      </svg>
    );
  }
  if (id === 'bars') {
    return (
      <svg width="42" height="26" viewBox="0 0 42 26" aria-hidden="true">
        <g stroke={up} strokeWidth="1.6">
          <line x1="10" y1="5" x2="10" y2="21" /><line x1="6" y1="9" x2="10" y2="9" />
          <line x1="10" y1="16" x2="14" y2="16" />
        </g>
        <g stroke={down} strokeWidth="1.6">
          <line x1="28" y1="3" x2="28" y2="19" /><line x1="24" y1="7" x2="28" y2="7" />
          <line x1="28" y1="15" x2="32" y2="15" />
        </g>
      </svg>
    );
  }
  const hollow = id === 'hollow';
  return (
    <svg width="42" height="26" viewBox="0 0 42 26" aria-hidden="true">
      <line x1="12" y1="3" x2="12" y2="23" stroke={up} strokeWidth="1.4" />
      <rect x="8" y="8" width="8" height="10" fill={hollow ? 'none' : up} stroke={up} strokeWidth="1.4" />
      <line x1="30" y1="4" x2="30" y2="22" stroke={down} strokeWidth="1.4" />
      <rect x="26" y="9" width="8" height="9" fill={down} stroke={down} strokeWidth="1.4" />
    </svg>
  );
}
